<template>
  <div
    class="form-icon-picker"
    :class="{
      'is-invalid': state === false,
      disabled: $attrs.disabled === ''
    }"
  >
    <BFormGroup
      :disabled="$attrs.disabled"
      :label="label"
      :label-for="$attrs.id"
      :invalid-feedback="errorMessage"
      :label-class="{
        'is-invalid': state === false
      }"
    >
      <Popper
        ref="popper"
        trigger="clickToOpen"
        :options="popperOptions"
        :disabled="$attrs.disabled"
        :visible-arrow="false"
        @show="isListVisible = true"
      >
        <div class="popper">
          <div
            v-if="isListVisible"
            class="form-icon-picker-list"
          >
            <span
              v-for="icon in icons"
              :key="icon.id"
              :class="{
                [`icon ${icon.class}`]: true,
                selected: value === icon.id
              }"
              @click="input(icon.id)"
            />
          </div>
        </div>

        <template slot="reference">
          <BInputGroup>
            <BFormInput
              :value="value"
              :state="state"
              :aria-label="label || $attrs.placeholder"
              v-bind="$attrs"
              v-on="listeners"
              @input="input"
            />

            <template v-slot:prepend>
              <div class="form-icon-picker-box">
                <span
                  v-if="currentIcon"
                  :class="currentIcon.class"
                />
              </div>
            </template>
          </BInputGroup>
        </template>
      </Popper>
    </BFormGroup>
  </div>
</template>

<script>
import Popper from 'vue-popperjs'
import {BFormGroup} from 'bootstrap-vue/src/components/form-group/form-group'
import {BInputGroup} from 'bootstrap-vue/src/components/input-group/input-group'
import {BFormInput} from 'bootstrap-vue/src/components/form-input/form-input'

export default {
  components: {
    BFormGroup,
    BInputGroup,
    BFormInput,
    Popper,
  },
  inheritAttrs: false,
  props: {
    /**
     * The current value of the icons. Result will always be a string.
     */
    value: {
      type: [String, Number],
      default: undefined,
    },

    /**
     * Text to place in the label/legend of the form group
     */
    label: {
      type: String,
      default: undefined,
    },

    /**
     * List of available icons
     */
    icons: {
      type: Array,
      required: true,
    },

    /**
     * Controls the validation state appearance of the component. 'true' for valid, 'false' or 'null' for no validation state
     */
    isInvalid: {
      type: Boolean,
      default: false,
    },

    /**
     * Text to show when the form group has an invalid state
     */
    errorMessage: {
      type: String,
      default: undefined,
    },
  },

  data: () => ({
    isListVisible: false,
    popperOptions: {
      placement: 'bottom-start',
      modifiers: {
        flip: {
          behavior: ['bottom-start', 'top-start'],
        },
      },
    },
  }),

  computed: {
    currentIcon () {
      return this.icons.find((i) => i.id === this.value)
    },
    state () {
      if (this.errorMessage && this.errorMessage.length > 0) {
        return false
      } else if (this.isInvalid) {
        return false
      } else {
        return null
      }
    },
    listeners () {
      const {input, ...listeners} = this.$listeners
      return listeners
    },
  },

  methods: {
    input (icon) {
      this.$emit('input', icon)

      this.$refs['popper'].doClose()
    },
  },
}
</script>

<style lang="scss">


.form-icon-picker {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;

  &.is-invalid {
    .form-icon-picker-box {
      border-color: $red-500 !important;
    }
  }

  &.disabled {
    pointer-events: none;

    .input-group-prepend {
      background-color: $gray-100 !important;
    }

    .form-icon-picker-box {
      color: $gray-300;
    }
  }

  .form-control {
    border-left: none !important;

    &:disabled {
      pointer-events: none;
    }

    &:hover:not(.is-invalid):not(:invalid) {
      border-color: transparent;
    }

    &:focus {
      border-color: $gray-200;
      padding: $input-padding-y $input-padding-x;
      border-width: 1px;
    }

    &[readonly] {
      ~ .form-icon-picker-box {
        background-color: $gray-000;
      }
    }
  }

  &:hover {
    .form-control,
    .form-control:focus {
      border-color: $gray-700 !important;
    }

    .form-icon-picker-box {
      border-color: $gray-700 !important;
      color: $gray-800;
    }
  }

  .input-group-prepend {
    margin-right: 0;
  }

  &-box {
    color: $gray-900;
    border: 1px solid $gray-200 !important;
    padding: 0 1rem;
    width: 40px;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    border-top-left-radius: $btn-border-radius;
    border-bottom-left-radius: $btn-border-radius;

    @include transition($input-transition);

    &:focus,
    &.focus {
      outline: 0;
    }

    // Disabled comes first so active can properly restyle
    &.disabled,
    &:disabled {
      color: $gray-300;
      background-color: $gray-100;
      border-color: $gray-200;
      cursor: default;
    }
  }

  &-list {
    width: 256px;
    height: 228px;
    background-color: #fff;
    overflow-y: auto;
    border-radius: 2px;
    box-shadow: 0 2px 8px 0 rgba(37, 37, 37, 0.16), 0 0 4px 0 rgba(37, 37, 37, 0.08);
    padding: 0.5rem;
    display: flex;
    flex-wrap: wrap;

    .icon {
      font-size: $font-size-base;
      width: 40px;
      height: 40px;
      border-radius: 2px;
      border: 2px solid transparent;
      justify-content: center;
      align-items: center;
      display: inline-flex;
      cursor: pointer;

      &:hover {
        background-color: $cyan-100;
        border-color: $cyan-100;
        color: $cyan-700;
      }

      &:focus {
        border-color: $cyan-500;
        outline: none;
      }

      &.disabled {
        color: $gray-300;
      }

      &.selected {
        background-color: $cyan-700;
        border-color: $cyan-700;
        color: #fff;
      }
    }
  }

  .popper {
    background: none;
    border: none;
    padding: 0;
    box-shadow: none;
  }
}
</style>
